import React, { useState, useEffect } from "react";
import { useCart } from "../components/Cart/cartContext";
import {
  CartContainer,
  CartItem,
  ProductImage,
  ProductInfo,
  CheckoutButton,
  TotalSection,
  CartSummary,
  EmptyCart,
  EditDetailsButton,
  PromoRow,
  PromoInput,
  PromoButton,
  PromoTag,
  PromoError
} from "../styles/cartStyle";
import { FaPlus, FaMinus, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePaystackPayment } from "react-paystack";
import UserDetailsModal from "../components/Cart/userDetails";
import ImageWithPlaceholder from "../components/common/ImageWithPlaceholder";
import { API_URL, PAYSTACK_PUBLIC_KEY } from "../config";

// type "percent": value is % of subtotal; "fixed": value is ₦ off. Never exceeds the subtotal.
const computeDiscount = (applied, subtotal) => {
  if (!applied || subtotal <= 0) return 0;
  const amount =
    applied.type === "percent"
      ? Math.round((subtotal * applied.value) / 100)
      : applied.value;
  return Math.min(amount, subtotal);
};


const Cart = () => {
  const { cart, setCart } = useCart();
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [userDetailsEntered, setUserDetailsEntered] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [userAddress, setUserAddress] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [userPhoneNumber, setUserPhoneNumber] = useState('');
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState(null);
  const [codeError, setCodeError] = useState("");
  // Order created by the backend before Paystack opens — its total is the
  // charged amount, computed from database prices (tamper-proof).
  const [serverOrder, setServerOrder] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [payRequested, setPayRequested] = useState(false);


  // Lines are addressed by index: the same product can appear twice in
  // different colours, so the product id alone is not a unique key.
  const incrementQuantity = (index) => {
    setCart(cart.map((item, i) => (i === index ? { ...item, quantity: item.quantity + 1 } : item)));
  };

  const decrementQuantity = (index) => {
    setCart(
      cart.map((item, i) =>
        i === index && item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : item
      )
    );
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // const total = cart.reduce(
  //   (acc, curr) => acc + curr.product.price * curr.quantity,
  //   0
  // );
  const handlePayNow = () => {
    setShowUserDetailsModal(true);
  };

  // Confirming details flows straight into payment — one step, no extra click
  const handleUserDetailsConfirm = (details) => {
    setUserEmail(details.email);
    setSelectedLocation(details.location);
    setDeliveryFee(details.fee);
    setUserDetailsEntered(true);
    setShowUserDetailsModal(false);
    setUserAddress(details.address);
    setUserFullName(details.fullName);
    setUserPhoneNumber(details.phone);
    startCheckout(details);
  };

  const subtotal = cart.reduce((acc, curr) => acc + curr.product.price * curr.quantity, 0);
  const discountAmount = computeDiscount(appliedCode, subtotal);
  const totalWithDelivery = Math.max(0, subtotal - discountAmount) + deliveryFee;

  const applyCode = async (e) => {
    if (e) e.preventDefault();
    const code = codeInput.trim();
    if (!code) return;
    try {
      // Codes are managed in the backoffice and validated by the backend
      const response = await fetch(
        `${API_URL}/discounts/validate/${encodeURIComponent(code)}`
      );
      if (response.ok) {
        setAppliedCode(await response.json());
        setCodeError("");
      } else {
        setAppliedCode(null);
        setCodeError("That code isn't valid. Try another.");
      }
    } catch {
      setAppliedCode(null);
      setCodeError("Couldn't check the code right now — please try again.");
    }
  };

  const removeCode = () => {
    setAppliedCode(null);
    setCodeInput("");
    setCodeError("");
  };


  const handleEditDetails = () => {
    setShowUserDetailsModal(true); // Re-open the modal for editing
  };

  const sendOrderDetails = async () => {
    const orderDetails = {
      userEmail,
      selectedLocation,
      items: cart.map(
        (item) =>
          `${item.product.name}${item.color ? ` (${item.color})` : ""} x ${item.quantity}`
      ),
      totalAmount: totalWithDelivery,
      discountCode: appliedCode ? appliedCode.code : "None",
      discountAmount,
      deliveryFee,
      userAddress,
      userFullName,
      userPhoneNumber
    };

    try {
      const response = await fetch('https://formspree.io/f/xeqbbejn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderDetails)
      });

      if (response.ok) {
        console.log('Order details sent successfully');
      } else {
        console.error('Error sending order details');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Step 1: the backend creates the order and returns the exact amount to
  // charge (prices/discount/delivery all recomputed server-side).
  // `details` is passed when coming straight from the details modal, since
  // React state won't have settled yet by then.
  const startCheckout = async (details) => {
    const customer = details
      ? {
          fullName: details.fullName,
          email: details.email,
          phone: details.phone,
          address: details.address,
          location: details.location,
        }
      : {
          fullName: userFullName,
          email: userEmail,
          phone: userPhoneNumber,
          address: userAddress,
          location: selectedLocation,
        };
    setCheckoutError("");
    setCreatingOrder(true);
    try {
      const response = await fetch(`${API_URL}/orders/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: cart.map((item) => ({
            productId: item.product._id || item.product.id,
            quantity: item.quantity,
            color: item.color || "",
          })),
          customer,
          discountCode: appliedCode ? appliedCode.code : null,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Could not start checkout. Please try again.");
      }
      setServerOrder(data);
      setPayRequested(true); // opens Paystack once state settles
    } catch (err) {
      setCheckoutError(err.message);
    } finally {
      setCreatingOrder(false);
    }
  };

  // Step 2: Paystack popup, charged with the server-computed amount.
  const initializePayment = usePaystackPayment({
    email: userEmail,
    amount: serverOrder?.amountKobo || 0,
    publicKey: PAYSTACK_PUBLIC_KEY,
    // let shoppers pay by card, direct bank transfer, bank account or USSD —
    // all verified by Paystack exactly like card payments
    channels: ["card", "bank_transfer", "bank", "ussd", "qr", "mobile_money"],
  });

  // Step 3: report the payment reference so the backend can verify it with
  // Paystack, record the order as paid, count the discount and update stock.
  // NOTE: must be a plain function, not async — Paystack's inline script
  // rejects async callbacks ("[object AsyncFunction]" fails its type check).
  const handlePaymentSuccess = (orderId) => (response) => {
    fetch(`${API_URL}/orders/${orderId}/paid`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reference: response.reference }),
    })
      .catch(() => {
        // Payment went through — the backoffice can still confirm manually
      })
      .finally(() => {
        sendOrderDetails(); // backup email notification (Formspree)
        setCart([]);
        setServerOrder(null);
        setPaymentSuccessful(true);
      });
  };

  useEffect(() => {
    if (payRequested && serverOrder) {
      setPayRequested(false);
      // react-paystack v4 takes positional args: (onSuccess, onClose)
      initializePayment(handlePaymentSuccess(serverOrder.orderId), () => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payRequested, serverOrder]);

  return (
    <CartContainer>
    <h2>Your Cart</h2>
    {paymentSuccessful && cart.length === 0 ? (
      <div>
        <h2>Thanks for shopping at Wear Lormami!</h2>
        <Link to="/new-arrivals" className="btn btn-primary">Shop Again</Link>
      </div>
    ) : cart.length === 0 ? (
      <EmptyCart>
        <p>Your cart is empty</p>
        <Link to="/new-arrivals">Continue Shopping</Link>
      </EmptyCart>
    ) : (
      <>
        {cart.map((item, index) => (
          <CartItem key={index}>
            <ImageWithPlaceholder
              as={ProductImage}
              src={item.product.image}
              alt={item.product.name}
              wrapperStyle={{
                width: "clamp(78px, 24vw, 96px)",
                height: "clamp(100px, 30vw, 124px)",
                flexShrink: 0,
              }}
            />
            <ProductInfo>
              <p>
                {item.product.name}
                {item.color ? ` · ${item.color}` : ""} ₦
                {new Intl.NumberFormat("en-NG").format(item.product.price)}
                x {item.quantity}
              </p>
              <div>
                <button onClick={() => incrementQuantity(index)}><FaPlus /></button>
                <button onClick={() => decrementQuantity(index)}><FaMinus /></button>
                <button onClick={() => removeFromCart(index)}><FaTrash /></button>
              </div>
            </ProductInfo>
          </CartItem>
        ))}
        <CartSummary>
          {appliedCode ? (
            <PromoTag>
              <span>
                Code <strong>{appliedCode.code}</strong> applied — {appliedCode.label}
              </span>
              <button type="button" onClick={removeCode}>Remove</button>
            </PromoTag>
          ) : (
            <PromoRow onSubmit={applyCode}>
              <PromoInput
                type="text"
                placeholder="Discount code"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                aria-label="Discount code"
              />
              <PromoButton type="submit">Apply</PromoButton>
              {codeError && <PromoError>{codeError}</PromoError>}
            </PromoRow>
          )}
          <TotalSection>
            <p>Subtotal: ₦{new Intl.NumberFormat('en-NG').format(subtotal)}</p>
            {discountAmount > 0 && (
              <p>Discount ({appliedCode.code}): −₦{new Intl.NumberFormat('en-NG').format(discountAmount)}</p>
            )}
            <p>Delivery Fee: ₦{new Intl.NumberFormat('en-NG').format(deliveryFee)}</p>
            <p>Total: ₦{new Intl.NumberFormat('en-NG').format(totalWithDelivery)}</p>
          </TotalSection>
          {!userDetailsEntered && (
            <CheckoutButton onClick={handlePayNow}>Pay Now</CheckoutButton>
          )}
          {userDetailsEntered && userEmail && userAddress && (
            <TotalSection>
              <p>Delivery Location: {selectedLocation}</p>
              <p>Address: {userAddress}</p>
              <EditDetailsButton onClick={handleEditDetails}>Edit Details</EditDetailsButton>
              {checkoutError && <PromoError>{checkoutError}</PromoError>}
              <CheckoutButton onClick={() => startCheckout()} disabled={creatingOrder}>
                {creatingOrder ? "Preparing your order…" : "Pay Securely"}
              </CheckoutButton>
            </TotalSection>
          )}
        </CartSummary>
      </>
    )}
    <UserDetailsModal
      show={showUserDetailsModal}
      onHide={() => setShowUserDetailsModal(false)}
      onConfirm={handleUserDetailsConfirm}
    />
  </CartContainer>
  );
};

export default Cart;
